// The frozen client application question ids (SEP-263, decision D4).
//
// A question id keys the answer that a client gave. The submitted snapshot
// answers BY ID for ever (SEP-263.20), so a renamed id orphans a real client
// answer and a dropped id loses one. The bank may ADD an id. It may never drop
// or rename one.
//
// `client-application-questions.test.ts` pins the two lists below to the bank
// and fails the build for a drop or a rename. The pattern is
// `apps/web/src/lib/fact-find-frozen-ids.ts`, and it exists for the same
// reason.
//
// The lists hold the id set as it stood at the FIRST release of the bank, and
// they are sorted so that a reviewer reads a diff. A later ticket adds each id
// that it adds to the bank (SEP-293.1, SEP-293.2, SEP-293.4, SEP-293.5,
// SEP-293.6 and SEP-293.8 added theirs).
//
// A retired id stays in the list (SEP-293.3). The test asks that each frozen id
// is in the bank of SOME version of the form, and not that the current version
// holds it. Thus an older submission keeps the question of each answer.

/** Every question id of the bank at the first release. */
export const FROZEN_CLIENT_APPLICATION_QUESTION_IDS: readonly string[] = [
    'account_types',
    'account_usage',
    'account_usage_other',
    'appoint_nominated_representative',
    'asset_business_interests',
    'asset_business_interests_ownership',
    'asset_cash_held_within_isas',
    'asset_cash_savings_premium_bonds',
    'asset_cash_savings_premium_bonds_ownership',
    'asset_eis_vct_or_unquoted_value',
    'asset_explain_alternatives',
    'asset_explain_cash_deposits',
    'asset_explain_derivatives',
    'asset_explain_equities',
    'asset_explain_fixed_income',
    'asset_explain_funds',
    'asset_invested_alternatives',
    'asset_invested_cash_deposits',
    'asset_invested_derivatives',
    'asset_invested_equities',
    'asset_invested_fixed_income',
    'asset_invested_funds',
    'asset_main_property_value',
    'asset_main_property_value_ownership',
    'asset_other',
    'asset_other_detail',
    'asset_other_investments_held_within_isas',
    'asset_other_investments_value',
    'asset_other_investments_value_ownership',
    'asset_other_ownership',
    'asset_other_property_value',
    'asset_other_property_value_ownership',
    'asset_pension_dowgate_sipp',
    'asset_pension_occupational_or_other',
    'asset_pension_other_personal',
    'assets_include_eis_vct_or_unquoted',
    'bank_account_currency',
    'bank_account_name',
    'bank_account_number',
    'bank_bic_aba',
    'bank_iban',
    'bank_name',
    'bank_sort_code',
    'business_number',
    'capital_losses_amount',
    'capital_losses_to_carry_forward',
    'capital_withdrawal_amount',
    'capital_withdrawal_expected',
    'capital_withdrawal_frequency',
    'capital_withdrawal_purpose',
    'cgt_allowance_available',
    'cgt_allowance_detail',
    'commitment_discretionary_expenses',
    'commitment_discretionary_expenses_ownership',
    'commitment_essential_expenses',
    'commitment_essential_expenses_ownership',
    'commitment_mortgage_rent',
    'commitment_mortgage_rent_ownership',
    'commitment_other_significant_expenses',
    'commitment_other_significant_expenses_detail',
    'commitment_school_fees',
    'communication_needs',
    'communication_needs_detail',
    'contribution_source_of_funds',
    'country_of_birth',
    'country_of_nationality',
    'current_address_country',
    'current_address_county',
    'current_address_house_no_name',
    'current_address_moved_in_date',
    'current_address_postcode',
    'current_address_street',
    'current_address_town_city',
    'current_investment_objective',
    'date_of_birth',
    'declarations_accepted',
    'dependent_1_date_of_birth',
    'dependent_1_name',
    'dependent_1_nature',
    'dependent_1_relationship',
    'dependent_2_date_of_birth',
    'dependent_2_name',
    'dependent_2_nature',
    'dependent_2_relationship',
    'dependent_3_date_of_birth',
    'dependent_3_name',
    'dependent_3_nature',
    'dependent_3_relationship',
    'dependent_4_date_of_birth',
    'dependent_4_name',
    'dependent_4_nature',
    'dependent_4_relationship',
    'electronic_communications_consent',
    'email_address',
    'employer_name',
    'employment_status',
    'employment_status_other',
    'financial_industry_capacities',
    'financial_industry_capacity_other',
    'financial_industry_years_in_role',
    'financial_qualification_detail',
    'financial_qualification_other',
    'financial_qualification_types',
    'forenames',
    'gains_within_annual_exemption',
    'general_health',
    'general_health_detail',
    'has_financial_dependents',
    'has_financial_qualifications',
    'has_will',
    'home_number',
    'income_all_other',
    'income_all_other_ownership',
    'income_all_other_sources',
    'income_employment',
    'income_investment_outside_dowgate',
    'income_investment_outside_dowgate_ownership',
    'income_other_pension',
    'income_state_pension',
    'income_tax_band',
    'income_versus_expenditure',
    'intended_risk_profile',
    'invested_in_aim',
    'investment_decisions',
    'investment_objective',
    'investment_restrictions',
    'isa_bed_and_isa_planned',
    'isa_junior_requirements',
    'isa_junior_requirements_context',
    'isa_other_family_members',
    'isa_other_family_members_context',
    'isa_regular_contributions_required',
    'isa_subscribed_amount',
    'isa_subscribed_this_year',
    'job_title',
    'knowledgeable_investor',
    'liability_anticipated_mortgage_payoff_date',
    'liability_credit_card_debt',
    'liability_mortgage_outstanding',
    'liability_mortgage_outstanding_ownership',
    'liability_other_debt',
    'liability_other_debt_detail',
    'liability_other_debt_ownership',
    'lived_at_current_address_3_years',
    'lpa_registered',
    'major_life_event',
    'major_life_event_detail',
    'marital_status',
    'marketing_company_news_and_events',
    'marketing_market_commentary_and_insights',
    'mental_health',
    'mobile_number',
    'national_client_identifier',
    'nationality',
    'nominated_representative_address_country',
    'nominated_representative_address_county',
    'nominated_representative_address_house_no_name',
    'nominated_representative_address_postcode',
    'nominated_representative_address_street',
    'nominated_representative_address_town_city',
    'nominated_representative_business_number',
    'nominated_representative_date_of_birth',
    'nominated_representative_declarations_accepted',
    'nominated_representative_email_address',
    'nominated_representative_forenames',
    'nominated_representative_general_health',
    'nominated_representative_general_health_detail',
    'nominated_representative_has_financial_qualifications',
    'nominated_representative_home_number',
    'nominated_representative_invested_in_aim',
    'nominated_representative_knowledgeable_investor',
    'nominated_representative_life_changes',
    'nominated_representative_mobile_number',
    'nominated_representative_other_health_conditions',
    'nominated_representative_other_health_conditions_detail',
    'nominated_representative_other_product_experience',
    'nominated_representative_portfolio_duration',
    'nominated_representative_previous_address_country',
    'nominated_representative_previous_address_county',
    'nominated_representative_previous_address_house_no_name',
    'nominated_representative_previous_address_postcode',
    'nominated_representative_previous_address_street',
    'nominated_representative_previous_address_town_city',
    'nominated_representative_primary_tax_residency',
    'nominated_representative_product_experience_alternatives',
    'nominated_representative_product_experience_cash_deposits',
    'nominated_representative_product_experience_derivatives',
    'nominated_representative_product_experience_equities',
    'nominated_representative_product_experience_fixed_income',
    'nominated_representative_product_experience_funds',
    'nominated_representative_risk_capacity_for_loss',
    'nominated_representative_risk_comfortable_with_smaller_companies',
    'nominated_representative_risk_income_change_over_ten_years',
    'nominated_representative_risk_income_security',
    'nominated_representative_risk_investment_horizon',
    'nominated_representative_risk_liabilities_change_over_ten_years',
    'nominated_representative_risk_liquid_funds_months_of_expenses',
    'nominated_representative_risk_loss_that_prompts_move_to_cash',
    'nominated_representative_risk_prefer_fewer_ups_and_downs',
    'nominated_representative_risk_protection_over_returns',
    'nominated_representative_risk_response_to_quarter_fall',
    'nominated_representative_risk_understands_smaller_company_risks',
    'nominated_representative_surname',
    'nominated_representative_taken_part_in_placing_or_new_issue',
    'nominated_representative_tax_identifier',
    'nominated_representative_title',
    'nominated_representative_title_other',
    'nominated_representative_types',
    'nominated_representative_upcoming_operation',
    'nominated_representative_upcoming_operation_detail',
    'nominated_representative_worked_in_financial_industry',
    'ongoing_account_funding_amount',
    'ongoing_account_funding_expected',
    'ongoing_account_funding_frequency',
    'other_health_conditions',
    'other_health_conditions_detail',
    'other_product_experience',
    'owns_or_holds_significant_share_in_business',
    'portal_security_answer_childhood_road',
    'portal_security_answer_fathers_first_name',
    'portal_security_answer_favourite_colour',
    'portal_security_answer_first_car',
    'portal_security_answer_first_pet',
    'portal_security_answer_holiday_destination',
    'portal_security_answer_mothers_maiden_name',
    'portfolio_duration',
    'preferred_investment_manager',
    'previous_address_country',
    'previous_address_county',
    'previous_address_house_no_name',
    'previous_address_moved_in_date',
    'previous_address_postcode',
    'previous_address_street',
    'previous_address_tenure',
    'previous_address_town_city',
    'product_experience_alternatives',
    'product_experience_cash_deposits',
    'product_experience_derivatives',
    'product_experience_equities',
    'product_experience_fixed_income',
    'product_experience_funds',
    'professional_client_interest',
    'regular_withdrawals_amount',
    'regular_withdrawals_frequency',
    'regular_withdrawals_needed',
    'regular_withdrawals_portfolio',
    'risk_bearable_loss',
    'risk_capacity_for_loss',
    'risk_comfort_below_original_investment',
    'risk_comfortable_through_smaller_company_volatility',
    'risk_comfortable_with_smaller_companies',
    'risk_income_change_over_ten_years',
    'risk_income_security',
    'risk_investment_horizon',
    'risk_liabilities_change_over_ten_years',
    'risk_liquid_funds_months_of_expenses',
    'risk_loss_that_prompts_move_to_cash',
    'risk_prefer_fewer_ups_and_downs',
    'risk_protection_over_returns',
    'risk_response_to_quarter_fall',
    'risk_time_horizon',
    'risk_understands_smaller_company_risks',
    'second_country_of_nationality',
    'service_type',
    'source_of_funds_business_detail',
    'source_of_funds_employment_detail',
    'source_of_funds_gift_detail',
    'source_of_funds_inheritance_detail',
    'source_of_funds_other_detail',
    'source_of_funds_property_detail',
    'source_of_funds_savings_investments_detail',
    'source_of_wealth_business_detail',
    'source_of_wealth_employment_detail',
    'source_of_wealth_gift_detail',
    'source_of_wealth_inheritance_detail',
    'source_of_wealth_other_detail',
    'source_of_wealth_property_detail',
    'source_of_wealth_savings_investments_detail',
    'source_types',
    'support_needs',
    'surname',
    'taken_part_in_placing_or_new_issue',
    'tax_identification_number_unavailable_reason',
    'tax_residency_1_citizen',
    'tax_residency_1_country',
    'tax_residency_1_tax_resident',
    'tax_residency_1_tin',
    'tax_residency_2_citizen',
    'tax_residency_2_country',
    'tax_residency_2_tax_resident',
    'tax_residency_2_tin',
    'tax_residency_3_citizen',
    'tax_residency_3_country',
    'tax_residency_3_tax_resident',
    'tax_residency_3_tin',
    'third_address_country',
    'third_address_county',
    'third_address_house_no_name',
    'third_address_moved_in_date',
    'third_address_postcode',
    'third_address_street',
    'third_address_town_city',
    'third_party_address',
    'third_party_company_name',
    'third_party_correspondence',
    'third_party_correspondence_other',
    'third_party_email',
    'third_party_forenames',
    'third_party_postcode',
    'third_party_role',
    'third_party_role_other',
    'third_party_surname',
    'third_party_telephone',
    'third_party_title',
    'third_party_title_other',
    'title',
    'title_other',
    'town_of_birth',
    'trusted_contact',
    'trusted_contact_details',
    'upcoming_operation',
    'upcoming_operation_detail',
    'us_person',
    'will_executor_contact_details',
    'will_firm_contact_details',
    'will_firm_contact_name',
    'will_last_review_date',
    'will_law_firm',
    'withdrawals_essential',
    'withdrawals_essential_detail',
    'worked_in_finance_detail',
    'worked_in_financial_industry',
];

/**
 * Every statement id of the bank at the first release.
 *
 * A statement is the wording that an `acknowledgement` binds to, and a note is
 * wording the form prints that asks for no answer. A snapshot pins the tick AND
 * the statements it accepted, so these ids are frozen beside the question ids.
 *
 * The list holds no `declaration_6c`. SEP-263.39 removes the Schedule of
 * Charges line from declaration 6c on purpose, so that id never existed and it
 * must never appear.
 */
export const FROZEN_CLIENT_APPLICATION_STATEMENT_IDS: readonly string[] = [
    'client_classification_note_1',
    'client_classification_note_2',
    'client_classification_note_3',
    'data_protection_note',
    'declaration_1',
    'declaration_2',
    'declaration_3',
    'declaration_4',
    'declaration_5',
    'declaration_6',
    'declaration_6a',
    'declaration_6b',
    'declaration_6d',
    'declaration_7',
    'declaration_8',
    'declaration_9',
    'electronic_consent_1',
    'electronic_consent_2',
    'isa_considerations_note',
    'nominated_representative_declaration_1',
    'nominated_representative_declaration_2',
    'nominated_representative_declaration_3',
    'nominated_representative_declaration_4',
];

/** Every question id of the company Discretionary and Advisory form 1.1 (SEP-293). */
export const FROZEN_COMPANY_DISCRETIONARY_ADVISORY_QUESTION_IDS: readonly string[] = [
    'account_types',
    'additional_notes',
    'applicant_capacity',
    'authorised_signatory_1_name',
    'authorised_signatory_1_responsibility',
    'authorised_signatory_2_name',
    'authorised_signatory_2_responsibility',
    'authorised_signatory_3_name',
    'authorised_signatory_3_responsibility',
    'authorised_signatory_4_name',
    'authorised_signatory_4_responsibility',
    'authorised_signatory_5_name',
    'authorised_signatory_5_responsibility',
    'authorised_signatory_6_name',
    'authorised_signatory_6_responsibility',
    'bank_account_currency',
    'bank_account_name',
    'bank_account_number',
    'bank_bic_aba',
    'bank_iban',
    'bank_name',
    'bank_sort_code',
    'business_number',
    'capital_withdrawal_amount',
    'capital_withdrawal_expected',
    'capital_withdrawal_frequency',
    'capital_withdrawal_frequency_other',
    'company_asset_business_interests',
    'company_asset_company_property_value',
    'company_asset_deposits',
    'company_asset_other',
    'company_asset_other_detail',
    'company_asset_other_investments_value',
    'company_asset_other_property_value',
    'company_commitment_loan_repayments',
    'company_commitment_other_significant_expenses',
    'company_commitment_other_significant_expenses_detail',
    'company_correspondence_address_country',
    'company_correspondence_address_county',
    'company_correspondence_address_house_no_name',
    'company_correspondence_address_postcode',
    'company_correspondence_address_street',
    'company_correspondence_address_town_city',
    'company_country_of_incorporation',
    'company_date_of_incorporation',
    'company_email',
    'company_income_all_other',
    'company_income_all_other_sources',
    'company_income_investment',
    'company_lei',
    'company_liability_mortgage_outstanding',
    'company_liability_other_debt',
    'company_liability_other_debt_detail',
    'company_liability_tax',
    'company_name',
    'company_phone_number',
    'company_registered_address_country',
    'company_registered_address_county',
    'company_registered_address_different',
    'company_registered_address_house_no_name',
    'company_registered_address_postcode',
    'company_registered_address_street',
    'company_registered_address_town_city',
    'company_registration_number',
    'company_source_of_funds_business_property_detail',
    'company_source_of_funds_capital_raise_detail',
    'company_source_of_funds_funding_programme_detail',
    'company_source_of_funds_investments_detail',
    'company_source_of_funds_other_detail',
    'company_source_of_funds_retained_earnings_detail',
    'company_source_of_wealth_business_property_detail',
    'company_source_of_wealth_capital_raise_detail',
    'company_source_of_wealth_funding_programme_detail',
    'company_source_of_wealth_investments_detail',
    'company_source_of_wealth_other_detail',
    'company_source_of_wealth_retained_earnings_detail',
    'company_source_types',
    'company_status',
    'country_of_birth',
    'country_of_nationality',
    'current_address_country',
    'current_address_county',
    'current_address_house_no_name',
    'current_address_moved_in_date',
    'current_address_postcode',
    'current_address_street',
    'current_address_town_city',
    'date_of_birth',
    'declarations_accepted',
    'electronic_communications_consent',
    'email_address',
    'forenames',
    'general_health',
    'general_health_detail',
    'has_authorised_signatory_2',
    'has_authorised_signatory_3',
    'has_authorised_signatory_4',
    'has_authorised_signatory_5',
    'has_authorised_signatory_6',
    'has_financial_qualifications',
    'has_stakeholder_2',
    'has_stakeholder_3',
    'has_stakeholder_4',
    'has_stakeholder_5',
    'has_tax_residency_2',
    'has_tax_residency_3',
    'home_number',
    'invested_in_aim',
    'investment_objective',
    'investment_restrictions',
    'knowledgeable_investor',
    'marketing_company_news_and_events',
    'marketing_market_commentary_and_insights',
    'mobile_number',
    'national_client_identifier',
    'nominated_representative_declarations_accepted',
    'ongoing_account_funding_amount',
    'ongoing_account_funding_expected',
    'ongoing_account_funding_frequency',
    'ongoing_account_funding_frequency_other',
    'other_health_conditions',
    'other_health_conditions_detail',
    'other_product_experience',
    'portal_security_answer_childhood_road',
    'portal_security_answer_fathers_first_name',
    'portal_security_answer_favourite_colour',
    'portal_security_answer_first_car',
    'portal_security_answer_first_pet',
    'portal_security_answer_holiday_destination',
    'portal_security_answer_mothers_maiden_name',
    'portfolio_duration',
    'preferred_investment_manager',
    'previous_address_country',
    'previous_address_county',
    'previous_address_house_no_name',
    'previous_address_moved_in_date',
    'previous_address_postcode',
    'previous_address_street',
    'previous_address_town_city',
    'product_experience_alternatives',
    'product_experience_cash_deposits',
    'product_experience_derivatives',
    'product_experience_equities',
    'product_experience_fixed_income',
    'product_experience_funds',
    'regular_withdrawals_amount',
    'regular_withdrawals_frequency',
    'regular_withdrawals_frequency_other',
    'regular_withdrawals_needed',
    'risk_capacity_for_loss',
    'risk_comfortable_with_smaller_companies',
    'risk_income_change_over_ten_years',
    'risk_income_security',
    'risk_investment_horizon',
    'risk_liabilities_change_over_ten_years',
    'risk_liquid_funds_months_of_expenses',
    'risk_loss_that_prompts_move_to_cash',
    'risk_prefer_fewer_ups_and_downs',
    'risk_protection_over_returns',
    'risk_response_to_quarter_fall',
    'risk_understands_smaller_company_risks',
    'second_country_of_nationality',
    'service_type',
    'stakeholder_1_business_number',
    'stakeholder_1_capacity',
    'stakeholder_1_country_of_birth',
    'stakeholder_1_country_of_nationality',
    'stakeholder_1_current_address_country',
    'stakeholder_1_current_address_county',
    'stakeholder_1_current_address_house_no_name',
    'stakeholder_1_current_address_postcode',
    'stakeholder_1_current_address_street',
    'stakeholder_1_current_address_town_city',
    'stakeholder_1_date_of_birth',
    'stakeholder_1_email_address',
    'stakeholder_1_forenames',
    'stakeholder_1_has_tax_residency_2',
    'stakeholder_1_has_tax_residency_3',
    'stakeholder_1_home_number',
    'stakeholder_1_lived_at_current_address_3_years',
    'stakeholder_1_mobile_number',
    'stakeholder_1_national_client_identifier',
    'stakeholder_1_previous_address_country',
    'stakeholder_1_previous_address_county',
    'stakeholder_1_previous_address_house_no_name',
    'stakeholder_1_previous_address_postcode',
    'stakeholder_1_previous_address_street',
    'stakeholder_1_previous_address_town_city',
    'stakeholder_1_second_country_of_nationality',
    'stakeholder_1_surname',
    'stakeholder_1_tax_residency_1_citizen',
    'stakeholder_1_tax_residency_1_country',
    'stakeholder_1_tax_residency_1_tax_resident',
    'stakeholder_1_tax_residency_1_tin',
    'stakeholder_1_tax_residency_2_citizen',
    'stakeholder_1_tax_residency_2_country',
    'stakeholder_1_tax_residency_2_tax_resident',
    'stakeholder_1_tax_residency_2_tin',
    'stakeholder_1_tax_residency_3_citizen',
    'stakeholder_1_tax_residency_3_country',
    'stakeholder_1_tax_residency_3_tax_resident',
    'stakeholder_1_tax_residency_3_tin',
    'stakeholder_1_title',
    'stakeholder_1_title_other',
    'stakeholder_1_town_of_birth',
    'stakeholder_1_us_person',
    'stakeholder_2_business_number',
    'stakeholder_2_capacity',
    'stakeholder_2_country_of_birth',
    'stakeholder_2_country_of_nationality',
    'stakeholder_2_current_address_country',
    'stakeholder_2_current_address_county',
    'stakeholder_2_current_address_house_no_name',
    'stakeholder_2_current_address_postcode',
    'stakeholder_2_current_address_street',
    'stakeholder_2_current_address_town_city',
    'stakeholder_2_date_of_birth',
    'stakeholder_2_email_address',
    'stakeholder_2_forenames',
    'stakeholder_2_has_tax_residency_2',
    'stakeholder_2_has_tax_residency_3',
    'stakeholder_2_home_number',
    'stakeholder_2_lived_at_current_address_3_years',
    'stakeholder_2_mobile_number',
    'stakeholder_2_national_client_identifier',
    'stakeholder_2_previous_address_country',
    'stakeholder_2_previous_address_county',
    'stakeholder_2_previous_address_house_no_name',
    'stakeholder_2_previous_address_postcode',
    'stakeholder_2_previous_address_street',
    'stakeholder_2_previous_address_town_city',
    'stakeholder_2_second_country_of_nationality',
    'stakeholder_2_surname',
    'stakeholder_2_tax_residency_1_citizen',
    'stakeholder_2_tax_residency_1_country',
    'stakeholder_2_tax_residency_1_tax_resident',
    'stakeholder_2_tax_residency_1_tin',
    'stakeholder_2_tax_residency_2_citizen',
    'stakeholder_2_tax_residency_2_country',
    'stakeholder_2_tax_residency_2_tax_resident',
    'stakeholder_2_tax_residency_2_tin',
    'stakeholder_2_tax_residency_3_citizen',
    'stakeholder_2_tax_residency_3_country',
    'stakeholder_2_tax_residency_3_tax_resident',
    'stakeholder_2_tax_residency_3_tin',
    'stakeholder_2_title',
    'stakeholder_2_title_other',
    'stakeholder_2_town_of_birth',
    'stakeholder_2_us_person',
    'stakeholder_3_business_number',
    'stakeholder_3_capacity',
    'stakeholder_3_country_of_birth',
    'stakeholder_3_country_of_nationality',
    'stakeholder_3_current_address_country',
    'stakeholder_3_current_address_county',
    'stakeholder_3_current_address_house_no_name',
    'stakeholder_3_current_address_postcode',
    'stakeholder_3_current_address_street',
    'stakeholder_3_current_address_town_city',
    'stakeholder_3_date_of_birth',
    'stakeholder_3_email_address',
    'stakeholder_3_forenames',
    'stakeholder_3_has_tax_residency_2',
    'stakeholder_3_has_tax_residency_3',
    'stakeholder_3_home_number',
    'stakeholder_3_lived_at_current_address_3_years',
    'stakeholder_3_mobile_number',
    'stakeholder_3_national_client_identifier',
    'stakeholder_3_previous_address_country',
    'stakeholder_3_previous_address_county',
    'stakeholder_3_previous_address_house_no_name',
    'stakeholder_3_previous_address_postcode',
    'stakeholder_3_previous_address_street',
    'stakeholder_3_previous_address_town_city',
    'stakeholder_3_second_country_of_nationality',
    'stakeholder_3_surname',
    'stakeholder_3_tax_residency_1_citizen',
    'stakeholder_3_tax_residency_1_country',
    'stakeholder_3_tax_residency_1_tax_resident',
    'stakeholder_3_tax_residency_1_tin',
    'stakeholder_3_tax_residency_2_citizen',
    'stakeholder_3_tax_residency_2_country',
    'stakeholder_3_tax_residency_2_tax_resident',
    'stakeholder_3_tax_residency_2_tin',
    'stakeholder_3_tax_residency_3_citizen',
    'stakeholder_3_tax_residency_3_country',
    'stakeholder_3_tax_residency_3_tax_resident',
    'stakeholder_3_tax_residency_3_tin',
    'stakeholder_3_title',
    'stakeholder_3_title_other',
    'stakeholder_3_town_of_birth',
    'stakeholder_3_us_person',
    'stakeholder_4_business_number',
    'stakeholder_4_capacity',
    'stakeholder_4_country_of_birth',
    'stakeholder_4_country_of_nationality',
    'stakeholder_4_current_address_country',
    'stakeholder_4_current_address_county',
    'stakeholder_4_current_address_house_no_name',
    'stakeholder_4_current_address_postcode',
    'stakeholder_4_current_address_street',
    'stakeholder_4_current_address_town_city',
    'stakeholder_4_date_of_birth',
    'stakeholder_4_email_address',
    'stakeholder_4_forenames',
    'stakeholder_4_has_tax_residency_2',
    'stakeholder_4_has_tax_residency_3',
    'stakeholder_4_home_number',
    'stakeholder_4_lived_at_current_address_3_years',
    'stakeholder_4_mobile_number',
    'stakeholder_4_national_client_identifier',
    'stakeholder_4_previous_address_country',
    'stakeholder_4_previous_address_county',
    'stakeholder_4_previous_address_house_no_name',
    'stakeholder_4_previous_address_postcode',
    'stakeholder_4_previous_address_street',
    'stakeholder_4_previous_address_town_city',
    'stakeholder_4_second_country_of_nationality',
    'stakeholder_4_surname',
    'stakeholder_4_tax_residency_1_citizen',
    'stakeholder_4_tax_residency_1_country',
    'stakeholder_4_tax_residency_1_tax_resident',
    'stakeholder_4_tax_residency_1_tin',
    'stakeholder_4_tax_residency_2_citizen',
    'stakeholder_4_tax_residency_2_country',
    'stakeholder_4_tax_residency_2_tax_resident',
    'stakeholder_4_tax_residency_2_tin',
    'stakeholder_4_tax_residency_3_citizen',
    'stakeholder_4_tax_residency_3_country',
    'stakeholder_4_tax_residency_3_tax_resident',
    'stakeholder_4_tax_residency_3_tin',
    'stakeholder_4_title',
    'stakeholder_4_title_other',
    'stakeholder_4_town_of_birth',
    'stakeholder_4_us_person',
    'stakeholder_5_business_number',
    'stakeholder_5_capacity',
    'stakeholder_5_country_of_birth',
    'stakeholder_5_country_of_nationality',
    'stakeholder_5_current_address_country',
    'stakeholder_5_current_address_county',
    'stakeholder_5_current_address_house_no_name',
    'stakeholder_5_current_address_postcode',
    'stakeholder_5_current_address_street',
    'stakeholder_5_current_address_town_city',
    'stakeholder_5_date_of_birth',
    'stakeholder_5_email_address',
    'stakeholder_5_forenames',
    'stakeholder_5_has_tax_residency_2',
    'stakeholder_5_has_tax_residency_3',
    'stakeholder_5_home_number',
    'stakeholder_5_lived_at_current_address_3_years',
    'stakeholder_5_mobile_number',
    'stakeholder_5_national_client_identifier',
    'stakeholder_5_previous_address_country',
    'stakeholder_5_previous_address_county',
    'stakeholder_5_previous_address_house_no_name',
    'stakeholder_5_previous_address_postcode',
    'stakeholder_5_previous_address_street',
    'stakeholder_5_previous_address_town_city',
    'stakeholder_5_second_country_of_nationality',
    'stakeholder_5_surname',
    'stakeholder_5_tax_residency_1_citizen',
    'stakeholder_5_tax_residency_1_country',
    'stakeholder_5_tax_residency_1_tax_resident',
    'stakeholder_5_tax_residency_1_tin',
    'stakeholder_5_tax_residency_2_citizen',
    'stakeholder_5_tax_residency_2_country',
    'stakeholder_5_tax_residency_2_tax_resident',
    'stakeholder_5_tax_residency_2_tin',
    'stakeholder_5_tax_residency_3_citizen',
    'stakeholder_5_tax_residency_3_country',
    'stakeholder_5_tax_residency_3_tax_resident',
    'stakeholder_5_tax_residency_3_tin',
    'stakeholder_5_title',
    'stakeholder_5_title_other',
    'stakeholder_5_town_of_birth',
    'stakeholder_5_us_person',
    'surname',
    'taken_part_in_placing_or_new_issue',
    'tax_residency_1_citizen',
    'tax_residency_1_country',
    'tax_residency_1_tax_resident',
    'tax_residency_1_tin',
    'tax_residency_2_citizen',
    'tax_residency_2_country',
    'tax_residency_2_tax_resident',
    'tax_residency_2_tin',
    'tax_residency_3_citizen',
    'tax_residency_3_country',
    'tax_residency_3_tax_resident',
    'tax_residency_3_tin',
    'third_address_country',
    'third_address_county',
    'third_address_house_no_name',
    'third_address_moved_in_date',
    'third_address_postcode',
    'third_address_street',
    'third_address_town_city',
    'third_party_address',
    'third_party_company_name',
    'third_party_correspondence',
    'third_party_correspondence_other',
    'third_party_email',
    'third_party_forenames',
    'third_party_postcode',
    'third_party_role',
    'third_party_role_other',
    'third_party_surname',
    'third_party_telephone',
    'third_party_title',
    'third_party_title_other',
    'title',
    'title_other',
    'town_of_birth',
    'upcoming_operation',
    'upcoming_operation_detail',
    'us_person',
    'worked_in_financial_industry',
];

/** Every statement id of the company Discretionary and Advisory form 1.1 (SEP-293). */
export const FROZEN_COMPANY_DISCRETIONARY_ADVISORY_STATEMENT_IDS: readonly string[] = [
    'company_client_classification_note_1',
    'company_client_classification_note_2',
    'company_client_classification_note_3',
    'company_client_classification_note_4',
    'company_declaration_1',
    'company_declaration_2',
    'company_declaration_3',
    'company_declaration_4',
    'company_declaration_5',
    'company_declaration_6',
    'company_declaration_6a',
    'company_declaration_6b',
    'company_declaration_6d',
    'company_declaration_7',
    'company_declaration_8',
    'company_declaration_9',
    'company_representatives_note_1',
    'company_representatives_note_2',
    'company_representatives_note_3',
    'company_service_note_advisory',
    'company_service_note_discretionary',
    'data_protection_note',
    'electronic_consent_1',
    'electronic_consent_2',
    'nominated_representative_declaration_1',
    'nominated_representative_declaration_2',
    'nominated_representative_declaration_3',
    'nominated_representative_declaration_4',
];

/** Every question id of the company Execution Only form 1.0 (SEP-293). */
export const FROZEN_COMPANY_EXECUTION_ONLY_QUESTION_IDS: readonly string[] = [
    'account_types',
    'additional_notes',
    'applicant_capacity',
    'authorised_signatory_1_name',
    'authorised_signatory_1_responsibility',
    'authorised_signatory_2_name',
    'authorised_signatory_2_responsibility',
    'authorised_signatory_3_name',
    'authorised_signatory_3_responsibility',
    'authorised_signatory_4_name',
    'authorised_signatory_4_responsibility',
    'authorised_signatory_5_name',
    'authorised_signatory_5_responsibility',
    'authorised_signatory_6_name',
    'authorised_signatory_6_responsibility',
    'bank_account_currency',
    'bank_account_name',
    'bank_account_number',
    'bank_bic_aba',
    'bank_iban',
    'bank_name',
    'bank_sort_code',
    'business_number',
    'capital_withdrawal_amount',
    'capital_withdrawal_expected',
    'capital_withdrawal_frequency',
    'capital_withdrawal_frequency_other',
    'company_correspondence_address_country',
    'company_correspondence_address_county',
    'company_correspondence_address_house_no_name',
    'company_correspondence_address_postcode',
    'company_correspondence_address_street',
    'company_correspondence_address_town_city',
    'company_country_of_incorporation',
    'company_date_of_incorporation',
    'company_email',
    'company_lei',
    'company_name',
    'company_phone_number',
    'company_registered_address_country',
    'company_registered_address_county',
    'company_registered_address_different',
    'company_registered_address_house_no_name',
    'company_registered_address_postcode',
    'company_registered_address_street',
    'company_registered_address_town_city',
    'company_registration_number',
    'company_source_of_funds_business_property_detail',
    'company_source_of_funds_capital_raise_detail',
    'company_source_of_funds_funding_programme_detail',
    'company_source_of_funds_investments_detail',
    'company_source_of_funds_other_detail',
    'company_source_of_funds_retained_earnings_detail',
    'company_source_of_wealth_business_property_detail',
    'company_source_of_wealth_capital_raise_detail',
    'company_source_of_wealth_funding_programme_detail',
    'company_source_of_wealth_investments_detail',
    'company_source_of_wealth_other_detail',
    'company_source_of_wealth_retained_earnings_detail',
    'company_source_types',
    'company_status',
    'country_of_birth',
    'country_of_nationality',
    'current_address_country',
    'current_address_county',
    'current_address_house_no_name',
    'current_address_moved_in_date',
    'current_address_postcode',
    'current_address_street',
    'current_address_town_city',
    'date_of_birth',
    'declarations_accepted',
    'electronic_communications_consent',
    'email_address',
    'forenames',
    'general_health',
    'general_health_detail',
    'has_authorised_signatory_2',
    'has_authorised_signatory_3',
    'has_authorised_signatory_4',
    'has_authorised_signatory_5',
    'has_authorised_signatory_6',
    'has_stakeholder_2',
    'has_stakeholder_3',
    'has_stakeholder_4',
    'has_stakeholder_5',
    'has_tax_residency_2',
    'has_tax_residency_3',
    'home_number',
    'marketing_company_news_and_events',
    'marketing_market_commentary_and_insights',
    'mobile_number',
    'national_client_identifier',
    'nominated_representative_declarations_accepted',
    'ongoing_account_funding_amount',
    'ongoing_account_funding_expected',
    'ongoing_account_funding_frequency',
    'ongoing_account_funding_frequency_other',
    'other_health_conditions',
    'other_health_conditions_detail',
    'portal_security_answer_childhood_road',
    'portal_security_answer_fathers_first_name',
    'portal_security_answer_favourite_colour',
    'portal_security_answer_first_car',
    'portal_security_answer_first_pet',
    'portal_security_answer_holiday_destination',
    'portal_security_answer_mothers_maiden_name',
    'preferred_investment_manager',
    'previous_address_country',
    'previous_address_county',
    'previous_address_house_no_name',
    'previous_address_moved_in_date',
    'previous_address_postcode',
    'previous_address_street',
    'previous_address_town_city',
    'regular_withdrawals_amount',
    'regular_withdrawals_frequency',
    'regular_withdrawals_frequency_other',
    'regular_withdrawals_needed',
    'second_country_of_nationality',
    'service_type',
    'stakeholder_1_business_number',
    'stakeholder_1_capacity',
    'stakeholder_1_country_of_birth',
    'stakeholder_1_country_of_nationality',
    'stakeholder_1_current_address_country',
    'stakeholder_1_current_address_county',
    'stakeholder_1_current_address_house_no_name',
    'stakeholder_1_current_address_postcode',
    'stakeholder_1_current_address_street',
    'stakeholder_1_current_address_town_city',
    'stakeholder_1_date_of_birth',
    'stakeholder_1_email_address',
    'stakeholder_1_forenames',
    'stakeholder_1_has_tax_residency_2',
    'stakeholder_1_has_tax_residency_3',
    'stakeholder_1_home_number',
    'stakeholder_1_lived_at_current_address_3_years',
    'stakeholder_1_mobile_number',
    'stakeholder_1_national_client_identifier',
    'stakeholder_1_previous_address_country',
    'stakeholder_1_previous_address_county',
    'stakeholder_1_previous_address_house_no_name',
    'stakeholder_1_previous_address_postcode',
    'stakeholder_1_previous_address_street',
    'stakeholder_1_previous_address_town_city',
    'stakeholder_1_second_country_of_nationality',
    'stakeholder_1_surname',
    'stakeholder_1_tax_residency_1_citizen',
    'stakeholder_1_tax_residency_1_country',
    'stakeholder_1_tax_residency_1_tax_resident',
    'stakeholder_1_tax_residency_1_tin',
    'stakeholder_1_tax_residency_2_citizen',
    'stakeholder_1_tax_residency_2_country',
    'stakeholder_1_tax_residency_2_tax_resident',
    'stakeholder_1_tax_residency_2_tin',
    'stakeholder_1_tax_residency_3_citizen',
    'stakeholder_1_tax_residency_3_country',
    'stakeholder_1_tax_residency_3_tax_resident',
    'stakeholder_1_tax_residency_3_tin',
    'stakeholder_1_title',
    'stakeholder_1_title_other',
    'stakeholder_1_town_of_birth',
    'stakeholder_1_us_person',
    'stakeholder_2_business_number',
    'stakeholder_2_capacity',
    'stakeholder_2_country_of_birth',
    'stakeholder_2_country_of_nationality',
    'stakeholder_2_current_address_country',
    'stakeholder_2_current_address_county',
    'stakeholder_2_current_address_house_no_name',
    'stakeholder_2_current_address_postcode',
    'stakeholder_2_current_address_street',
    'stakeholder_2_current_address_town_city',
    'stakeholder_2_date_of_birth',
    'stakeholder_2_email_address',
    'stakeholder_2_forenames',
    'stakeholder_2_has_tax_residency_2',
    'stakeholder_2_has_tax_residency_3',
    'stakeholder_2_home_number',
    'stakeholder_2_lived_at_current_address_3_years',
    'stakeholder_2_mobile_number',
    'stakeholder_2_national_client_identifier',
    'stakeholder_2_previous_address_country',
    'stakeholder_2_previous_address_county',
    'stakeholder_2_previous_address_house_no_name',
    'stakeholder_2_previous_address_postcode',
    'stakeholder_2_previous_address_street',
    'stakeholder_2_previous_address_town_city',
    'stakeholder_2_second_country_of_nationality',
    'stakeholder_2_surname',
    'stakeholder_2_tax_residency_1_citizen',
    'stakeholder_2_tax_residency_1_country',
    'stakeholder_2_tax_residency_1_tax_resident',
    'stakeholder_2_tax_residency_1_tin',
    'stakeholder_2_tax_residency_2_citizen',
    'stakeholder_2_tax_residency_2_country',
    'stakeholder_2_tax_residency_2_tax_resident',
    'stakeholder_2_tax_residency_2_tin',
    'stakeholder_2_tax_residency_3_citizen',
    'stakeholder_2_tax_residency_3_country',
    'stakeholder_2_tax_residency_3_tax_resident',
    'stakeholder_2_tax_residency_3_tin',
    'stakeholder_2_title',
    'stakeholder_2_title_other',
    'stakeholder_2_town_of_birth',
    'stakeholder_2_us_person',
    'stakeholder_3_business_number',
    'stakeholder_3_capacity',
    'stakeholder_3_country_of_birth',
    'stakeholder_3_country_of_nationality',
    'stakeholder_3_current_address_country',
    'stakeholder_3_current_address_county',
    'stakeholder_3_current_address_house_no_name',
    'stakeholder_3_current_address_postcode',
    'stakeholder_3_current_address_street',
    'stakeholder_3_current_address_town_city',
    'stakeholder_3_date_of_birth',
    'stakeholder_3_email_address',
    'stakeholder_3_forenames',
    'stakeholder_3_has_tax_residency_2',
    'stakeholder_3_has_tax_residency_3',
    'stakeholder_3_home_number',
    'stakeholder_3_lived_at_current_address_3_years',
    'stakeholder_3_mobile_number',
    'stakeholder_3_national_client_identifier',
    'stakeholder_3_previous_address_country',
    'stakeholder_3_previous_address_county',
    'stakeholder_3_previous_address_house_no_name',
    'stakeholder_3_previous_address_postcode',
    'stakeholder_3_previous_address_street',
    'stakeholder_3_previous_address_town_city',
    'stakeholder_3_second_country_of_nationality',
    'stakeholder_3_surname',
    'stakeholder_3_tax_residency_1_citizen',
    'stakeholder_3_tax_residency_1_country',
    'stakeholder_3_tax_residency_1_tax_resident',
    'stakeholder_3_tax_residency_1_tin',
    'stakeholder_3_tax_residency_2_citizen',
    'stakeholder_3_tax_residency_2_country',
    'stakeholder_3_tax_residency_2_tax_resident',
    'stakeholder_3_tax_residency_2_tin',
    'stakeholder_3_tax_residency_3_citizen',
    'stakeholder_3_tax_residency_3_country',
    'stakeholder_3_tax_residency_3_tax_resident',
    'stakeholder_3_tax_residency_3_tin',
    'stakeholder_3_title',
    'stakeholder_3_title_other',
    'stakeholder_3_town_of_birth',
    'stakeholder_3_us_person',
    'stakeholder_4_business_number',
    'stakeholder_4_capacity',
    'stakeholder_4_country_of_birth',
    'stakeholder_4_country_of_nationality',
    'stakeholder_4_current_address_country',
    'stakeholder_4_current_address_county',
    'stakeholder_4_current_address_house_no_name',
    'stakeholder_4_current_address_postcode',
    'stakeholder_4_current_address_street',
    'stakeholder_4_current_address_town_city',
    'stakeholder_4_date_of_birth',
    'stakeholder_4_email_address',
    'stakeholder_4_forenames',
    'stakeholder_4_has_tax_residency_2',
    'stakeholder_4_has_tax_residency_3',
    'stakeholder_4_home_number',
    'stakeholder_4_lived_at_current_address_3_years',
    'stakeholder_4_mobile_number',
    'stakeholder_4_national_client_identifier',
    'stakeholder_4_previous_address_country',
    'stakeholder_4_previous_address_county',
    'stakeholder_4_previous_address_house_no_name',
    'stakeholder_4_previous_address_postcode',
    'stakeholder_4_previous_address_street',
    'stakeholder_4_previous_address_town_city',
    'stakeholder_4_second_country_of_nationality',
    'stakeholder_4_surname',
    'stakeholder_4_tax_residency_1_citizen',
    'stakeholder_4_tax_residency_1_country',
    'stakeholder_4_tax_residency_1_tax_resident',
    'stakeholder_4_tax_residency_1_tin',
    'stakeholder_4_tax_residency_2_citizen',
    'stakeholder_4_tax_residency_2_country',
    'stakeholder_4_tax_residency_2_tax_resident',
    'stakeholder_4_tax_residency_2_tin',
    'stakeholder_4_tax_residency_3_citizen',
    'stakeholder_4_tax_residency_3_country',
    'stakeholder_4_tax_residency_3_tax_resident',
    'stakeholder_4_tax_residency_3_tin',
    'stakeholder_4_title',
    'stakeholder_4_title_other',
    'stakeholder_4_town_of_birth',
    'stakeholder_4_us_person',
    'stakeholder_5_business_number',
    'stakeholder_5_capacity',
    'stakeholder_5_country_of_birth',
    'stakeholder_5_country_of_nationality',
    'stakeholder_5_current_address_country',
    'stakeholder_5_current_address_county',
    'stakeholder_5_current_address_house_no_name',
    'stakeholder_5_current_address_postcode',
    'stakeholder_5_current_address_street',
    'stakeholder_5_current_address_town_city',
    'stakeholder_5_date_of_birth',
    'stakeholder_5_email_address',
    'stakeholder_5_forenames',
    'stakeholder_5_has_tax_residency_2',
    'stakeholder_5_has_tax_residency_3',
    'stakeholder_5_home_number',
    'stakeholder_5_lived_at_current_address_3_years',
    'stakeholder_5_mobile_number',
    'stakeholder_5_national_client_identifier',
    'stakeholder_5_previous_address_country',
    'stakeholder_5_previous_address_county',
    'stakeholder_5_previous_address_house_no_name',
    'stakeholder_5_previous_address_postcode',
    'stakeholder_5_previous_address_street',
    'stakeholder_5_previous_address_town_city',
    'stakeholder_5_second_country_of_nationality',
    'stakeholder_5_surname',
    'stakeholder_5_tax_residency_1_citizen',
    'stakeholder_5_tax_residency_1_country',
    'stakeholder_5_tax_residency_1_tax_resident',
    'stakeholder_5_tax_residency_1_tin',
    'stakeholder_5_tax_residency_2_citizen',
    'stakeholder_5_tax_residency_2_country',
    'stakeholder_5_tax_residency_2_tax_resident',
    'stakeholder_5_tax_residency_2_tin',
    'stakeholder_5_tax_residency_3_citizen',
    'stakeholder_5_tax_residency_3_country',
    'stakeholder_5_tax_residency_3_tax_resident',
    'stakeholder_5_tax_residency_3_tin',
    'stakeholder_5_title',
    'stakeholder_5_title_other',
    'stakeholder_5_town_of_birth',
    'stakeholder_5_us_person',
    'surname',
    'tax_residency_1_citizen',
    'tax_residency_1_country',
    'tax_residency_1_tax_resident',
    'tax_residency_1_tin',
    'tax_residency_2_citizen',
    'tax_residency_2_country',
    'tax_residency_2_tax_resident',
    'tax_residency_2_tin',
    'tax_residency_3_citizen',
    'tax_residency_3_country',
    'tax_residency_3_tax_resident',
    'tax_residency_3_tin',
    'third_address_country',
    'third_address_county',
    'third_address_house_no_name',
    'third_address_moved_in_date',
    'third_address_postcode',
    'third_address_street',
    'third_address_town_city',
    'third_party_address',
    'third_party_company_name',
    'third_party_correspondence',
    'third_party_correspondence_other',
    'third_party_email',
    'third_party_forenames',
    'third_party_postcode',
    'third_party_role',
    'third_party_role_other',
    'third_party_surname',
    'third_party_telephone',
    'third_party_title',
    'third_party_title_other',
    'title',
    'title_other',
    'town_of_birth',
    'upcoming_operation',
    'upcoming_operation_detail',
    'us_person',
];

/** Every statement id of the company Execution Only form 1.0 (SEP-293). */
export const FROZEN_COMPANY_EXECUTION_ONLY_STATEMENT_IDS: readonly string[] = [
    'company_declaration_1',
    'company_declaration_2',
    'company_declaration_3',
    'company_declaration_4',
    'company_declaration_5',
    'company_declaration_6',
    'company_declaration_6a',
    'company_declaration_6b',
    'company_declaration_6d',
    'company_declaration_7',
    'company_declaration_8',
    'company_declaration_9',
    'company_representatives_note_1',
    'company_representatives_note_4',
    'company_service_note_execution_only',
    'data_protection_note',
    'electronic_consent_1',
    'electronic_consent_2',
    'nominated_representative_declaration_1',
    'nominated_representative_declaration_2',
    'nominated_representative_declaration_3',
    'nominated_representative_declaration_4',
];

/** Every question id of the trust form 1.1 (SEP-293). */
export const FROZEN_TRUST_QUESTION_IDS: readonly string[] = [
    'account_types',
    'additional_notes',
    'applicant_capacity',
    'authorised_signatory_1_name',
    'authorised_signatory_1_responsibility',
    'authorised_signatory_2_name',
    'authorised_signatory_2_responsibility',
    'authorised_signatory_3_name',
    'authorised_signatory_3_responsibility',
    'authorised_signatory_4_name',
    'authorised_signatory_4_responsibility',
    'authorised_signatory_5_name',
    'authorised_signatory_5_responsibility',
    'authorised_signatory_6_name',
    'authorised_signatory_6_responsibility',
    'authorised_signatory_7_name',
    'authorised_signatory_7_responsibility',
    'authorised_signatory_8_name',
    'authorised_signatory_8_responsibility',
    'bank_account_currency',
    'bank_account_name',
    'bank_account_number',
    'bank_bic_aba',
    'bank_iban',
    'bank_name',
    'bank_sort_code',
    'business_number',
    'capital_withdrawal_amount',
    'capital_withdrawal_expected',
    'capital_withdrawal_frequency',
    'capital_withdrawal_frequency_other',
    'country_of_birth',
    'country_of_nationality',
    'current_address_country',
    'current_address_county',
    'current_address_house_no_name',
    'current_address_moved_in_date',
    'current_address_postcode',
    'current_address_street',
    'current_address_town_city',
    'date_of_birth',
    'declarations_accepted',
    'electronic_communications_consent',
    'email_address',
    'forenames',
    'general_health',
    'general_health_detail',
    'has_authorised_signatory_2',
    'has_authorised_signatory_3',
    'has_authorised_signatory_4',
    'has_authorised_signatory_5',
    'has_authorised_signatory_6',
    'has_authorised_signatory_7',
    'has_authorised_signatory_8',
    'has_financial_qualifications',
    'has_tax_residency_2',
    'has_tax_residency_3',
    'has_trust_party_2',
    'has_trust_party_3',
    'has_trust_party_4',
    'has_trust_party_5',
    'has_trust_party_6',
    'has_trust_party_7',
    'home_number',
    'invested_in_aim',
    'investment_objective',
    'investment_restrictions',
    'knowledgeable_investor',
    'marketing_company_news_and_events',
    'marketing_market_commentary_and_insights',
    'mobile_number',
    'national_client_identifier',
    'nominated_representative_declarations_accepted',
    'ongoing_account_funding_amount',
    'ongoing_account_funding_expected',
    'ongoing_account_funding_frequency',
    'ongoing_account_funding_frequency_other',
    'other_health_conditions',
    'other_health_conditions_detail',
    'other_product_experience',
    'portal_security_answer_childhood_road',
    'portal_security_answer_fathers_first_name',
    'portal_security_answer_favourite_colour',
    'portal_security_answer_first_car',
    'portal_security_answer_first_pet',
    'portal_security_answer_holiday_destination',
    'portal_security_answer_mothers_maiden_name',
    'portfolio_duration',
    'preferred_investment_manager',
    'previous_address_country',
    'previous_address_county',
    'previous_address_house_no_name',
    'previous_address_moved_in_date',
    'previous_address_postcode',
    'previous_address_street',
    'previous_address_town_city',
    'product_experience_alternatives',
    'product_experience_cash_deposits',
    'product_experience_derivatives',
    'product_experience_equities',
    'product_experience_fixed_income',
    'product_experience_funds',
    'regular_withdrawals_amount',
    'regular_withdrawals_frequency',
    'regular_withdrawals_frequency_other',
    'regular_withdrawals_needed',
    'risk_capacity_for_loss',
    'risk_comfortable_with_smaller_companies',
    'risk_income_change_over_ten_years',
    'risk_income_security',
    'risk_investment_horizon',
    'risk_liabilities_change_over_ten_years',
    'risk_liquid_funds_months_of_expenses',
    'risk_loss_that_prompts_move_to_cash',
    'risk_prefer_fewer_ups_and_downs',
    'risk_protection_over_returns',
    'risk_response_to_quarter_fall',
    'risk_understands_smaller_company_risks',
    'second_country_of_nationality',
    'service_type',
    'source_of_funds_business_detail',
    'source_of_funds_employment_detail',
    'source_of_funds_gift_detail',
    'source_of_funds_inheritance_detail',
    'source_of_funds_other_detail',
    'source_of_funds_property_detail',
    'source_of_funds_savings_investments_detail',
    'source_of_wealth_business_detail',
    'source_of_wealth_employment_detail',
    'source_of_wealth_gift_detail',
    'source_of_wealth_inheritance_detail',
    'source_of_wealth_other_detail',
    'source_of_wealth_property_detail',
    'source_of_wealth_savings_investments_detail',
    'source_types',
    'surname',
    'taken_part_in_placing_or_new_issue',
    'tax_residency_1_citizen',
    'tax_residency_1_country',
    'tax_residency_1_tax_resident',
    'tax_residency_1_tin',
    'tax_residency_2_citizen',
    'tax_residency_2_country',
    'tax_residency_2_tax_resident',
    'tax_residency_2_tin',
    'tax_residency_3_citizen',
    'tax_residency_3_country',
    'tax_residency_3_tax_resident',
    'tax_residency_3_tin',
    'third_address_country',
    'third_address_county',
    'third_address_house_no_name',
    'third_address_moved_in_date',
    'third_address_postcode',
    'third_address_street',
    'third_address_town_city',
    'third_party_address',
    'third_party_company_name',
    'third_party_correspondence',
    'third_party_correspondence_other',
    'third_party_email',
    'third_party_forenames',
    'third_party_postcode',
    'third_party_role',
    'third_party_role_other',
    'third_party_surname',
    'third_party_telephone',
    'third_party_title',
    'third_party_title_other',
    'title',
    'title_other',
    'town_of_birth',
    'trust_asset_cash_deposits',
    'trust_asset_other',
    'trust_asset_other_detail',
    'trust_asset_other_investments_value',
    'trust_asset_property_value',
    'trust_charity_number',
    'trust_commitment_administrative_expenses',
    'trust_commitment_distributions',
    'trust_commitment_loan_repayments',
    'trust_commitment_other_significant_expenses',
    'trust_commitment_other_significant_expenses_detail',
    'trust_correspondence_address_country',
    'trust_correspondence_address_county',
    'trust_correspondence_address_house_no_name',
    'trust_correspondence_address_postcode',
    'trust_correspondence_address_street',
    'trust_correspondence_address_town_city',
    'trust_country_of_establishment',
    'trust_date_established',
    'trust_email',
    'trust_income_all_other',
    'trust_income_all_other_sources',
    'trust_income_donations_grants',
    'trust_income_investment',
    'trust_lei_number',
    'trust_liability_mortgage_outstanding',
    'trust_liability_other_debt',
    'trust_liability_other_debt_detail',
    'trust_liability_tax',
    'trust_name',
    'trust_party_1_capacity',
    'trust_party_1_country_of_birth',
    'trust_party_1_country_of_nationality',
    'trust_party_1_current_address_country',
    'trust_party_1_current_address_county',
    'trust_party_1_current_address_house_no_name',
    'trust_party_1_current_address_postcode',
    'trust_party_1_current_address_street',
    'trust_party_1_current_address_town_city',
    'trust_party_1_date_of_birth',
    'trust_party_1_email_address',
    'trust_party_1_forenames',
    'trust_party_1_has_tax_residency_2',
    'trust_party_1_has_tax_residency_3',
    'trust_party_1_lived_at_current_address_3_years',
    'trust_party_1_national_client_identifier',
    'trust_party_1_previous_address_country',
    'trust_party_1_previous_address_county',
    'trust_party_1_previous_address_house_no_name',
    'trust_party_1_previous_address_postcode',
    'trust_party_1_previous_address_street',
    'trust_party_1_previous_address_town_city',
    'trust_party_1_second_country_of_nationality',
    'trust_party_1_surname',
    'trust_party_1_tax_residency_1_citizen',
    'trust_party_1_tax_residency_1_country',
    'trust_party_1_tax_residency_1_tax_resident',
    'trust_party_1_tax_residency_1_tin',
    'trust_party_1_tax_residency_2_citizen',
    'trust_party_1_tax_residency_2_country',
    'trust_party_1_tax_residency_2_tax_resident',
    'trust_party_1_tax_residency_2_tin',
    'trust_party_1_tax_residency_3_citizen',
    'trust_party_1_tax_residency_3_country',
    'trust_party_1_tax_residency_3_tax_resident',
    'trust_party_1_tax_residency_3_tin',
    'trust_party_1_telephone_number',
    'trust_party_1_title',
    'trust_party_1_title_other',
    'trust_party_1_town_of_birth',
    'trust_party_1_us_person',
    'trust_party_2_capacity',
    'trust_party_2_country_of_birth',
    'trust_party_2_country_of_nationality',
    'trust_party_2_current_address_country',
    'trust_party_2_current_address_county',
    'trust_party_2_current_address_house_no_name',
    'trust_party_2_current_address_postcode',
    'trust_party_2_current_address_street',
    'trust_party_2_current_address_town_city',
    'trust_party_2_date_of_birth',
    'trust_party_2_email_address',
    'trust_party_2_forenames',
    'trust_party_2_has_tax_residency_2',
    'trust_party_2_has_tax_residency_3',
    'trust_party_2_lived_at_current_address_3_years',
    'trust_party_2_national_client_identifier',
    'trust_party_2_previous_address_country',
    'trust_party_2_previous_address_county',
    'trust_party_2_previous_address_house_no_name',
    'trust_party_2_previous_address_postcode',
    'trust_party_2_previous_address_street',
    'trust_party_2_previous_address_town_city',
    'trust_party_2_second_country_of_nationality',
    'trust_party_2_surname',
    'trust_party_2_tax_residency_1_citizen',
    'trust_party_2_tax_residency_1_country',
    'trust_party_2_tax_residency_1_tax_resident',
    'trust_party_2_tax_residency_1_tin',
    'trust_party_2_tax_residency_2_citizen',
    'trust_party_2_tax_residency_2_country',
    'trust_party_2_tax_residency_2_tax_resident',
    'trust_party_2_tax_residency_2_tin',
    'trust_party_2_tax_residency_3_citizen',
    'trust_party_2_tax_residency_3_country',
    'trust_party_2_tax_residency_3_tax_resident',
    'trust_party_2_tax_residency_3_tin',
    'trust_party_2_telephone_number',
    'trust_party_2_title',
    'trust_party_2_title_other',
    'trust_party_2_town_of_birth',
    'trust_party_2_us_person',
    'trust_party_3_capacity',
    'trust_party_3_country_of_birth',
    'trust_party_3_country_of_nationality',
    'trust_party_3_current_address_country',
    'trust_party_3_current_address_county',
    'trust_party_3_current_address_house_no_name',
    'trust_party_3_current_address_postcode',
    'trust_party_3_current_address_street',
    'trust_party_3_current_address_town_city',
    'trust_party_3_date_of_birth',
    'trust_party_3_email_address',
    'trust_party_3_forenames',
    'trust_party_3_has_tax_residency_2',
    'trust_party_3_has_tax_residency_3',
    'trust_party_3_lived_at_current_address_3_years',
    'trust_party_3_national_client_identifier',
    'trust_party_3_previous_address_country',
    'trust_party_3_previous_address_county',
    'trust_party_3_previous_address_house_no_name',
    'trust_party_3_previous_address_postcode',
    'trust_party_3_previous_address_street',
    'trust_party_3_previous_address_town_city',
    'trust_party_3_second_country_of_nationality',
    'trust_party_3_surname',
    'trust_party_3_tax_residency_1_citizen',
    'trust_party_3_tax_residency_1_country',
    'trust_party_3_tax_residency_1_tax_resident',
    'trust_party_3_tax_residency_1_tin',
    'trust_party_3_tax_residency_2_citizen',
    'trust_party_3_tax_residency_2_country',
    'trust_party_3_tax_residency_2_tax_resident',
    'trust_party_3_tax_residency_2_tin',
    'trust_party_3_tax_residency_3_citizen',
    'trust_party_3_tax_residency_3_country',
    'trust_party_3_tax_residency_3_tax_resident',
    'trust_party_3_tax_residency_3_tin',
    'trust_party_3_telephone_number',
    'trust_party_3_title',
    'trust_party_3_title_other',
    'trust_party_3_town_of_birth',
    'trust_party_3_us_person',
    'trust_party_4_capacity',
    'trust_party_4_country_of_birth',
    'trust_party_4_country_of_nationality',
    'trust_party_4_current_address_country',
    'trust_party_4_current_address_county',
    'trust_party_4_current_address_house_no_name',
    'trust_party_4_current_address_postcode',
    'trust_party_4_current_address_street',
    'trust_party_4_current_address_town_city',
    'trust_party_4_date_of_birth',
    'trust_party_4_email_address',
    'trust_party_4_forenames',
    'trust_party_4_has_tax_residency_2',
    'trust_party_4_has_tax_residency_3',
    'trust_party_4_lived_at_current_address_3_years',
    'trust_party_4_national_client_identifier',
    'trust_party_4_previous_address_country',
    'trust_party_4_previous_address_county',
    'trust_party_4_previous_address_house_no_name',
    'trust_party_4_previous_address_postcode',
    'trust_party_4_previous_address_street',
    'trust_party_4_previous_address_town_city',
    'trust_party_4_second_country_of_nationality',
    'trust_party_4_surname',
    'trust_party_4_tax_residency_1_citizen',
    'trust_party_4_tax_residency_1_country',
    'trust_party_4_tax_residency_1_tax_resident',
    'trust_party_4_tax_residency_1_tin',
    'trust_party_4_tax_residency_2_citizen',
    'trust_party_4_tax_residency_2_country',
    'trust_party_4_tax_residency_2_tax_resident',
    'trust_party_4_tax_residency_2_tin',
    'trust_party_4_tax_residency_3_citizen',
    'trust_party_4_tax_residency_3_country',
    'trust_party_4_tax_residency_3_tax_resident',
    'trust_party_4_tax_residency_3_tin',
    'trust_party_4_telephone_number',
    'trust_party_4_title',
    'trust_party_4_title_other',
    'trust_party_4_town_of_birth',
    'trust_party_4_us_person',
    'trust_party_5_capacity',
    'trust_party_5_country_of_birth',
    'trust_party_5_country_of_nationality',
    'trust_party_5_current_address_country',
    'trust_party_5_current_address_county',
    'trust_party_5_current_address_house_no_name',
    'trust_party_5_current_address_postcode',
    'trust_party_5_current_address_street',
    'trust_party_5_current_address_town_city',
    'trust_party_5_date_of_birth',
    'trust_party_5_email_address',
    'trust_party_5_forenames',
    'trust_party_5_has_tax_residency_2',
    'trust_party_5_has_tax_residency_3',
    'trust_party_5_lived_at_current_address_3_years',
    'trust_party_5_national_client_identifier',
    'trust_party_5_previous_address_country',
    'trust_party_5_previous_address_county',
    'trust_party_5_previous_address_house_no_name',
    'trust_party_5_previous_address_postcode',
    'trust_party_5_previous_address_street',
    'trust_party_5_previous_address_town_city',
    'trust_party_5_second_country_of_nationality',
    'trust_party_5_surname',
    'trust_party_5_tax_residency_1_citizen',
    'trust_party_5_tax_residency_1_country',
    'trust_party_5_tax_residency_1_tax_resident',
    'trust_party_5_tax_residency_1_tin',
    'trust_party_5_tax_residency_2_citizen',
    'trust_party_5_tax_residency_2_country',
    'trust_party_5_tax_residency_2_tax_resident',
    'trust_party_5_tax_residency_2_tin',
    'trust_party_5_tax_residency_3_citizen',
    'trust_party_5_tax_residency_3_country',
    'trust_party_5_tax_residency_3_tax_resident',
    'trust_party_5_tax_residency_3_tin',
    'trust_party_5_telephone_number',
    'trust_party_5_title',
    'trust_party_5_title_other',
    'trust_party_5_town_of_birth',
    'trust_party_5_us_person',
    'trust_party_6_capacity',
    'trust_party_6_country_of_birth',
    'trust_party_6_country_of_nationality',
    'trust_party_6_current_address_country',
    'trust_party_6_current_address_county',
    'trust_party_6_current_address_house_no_name',
    'trust_party_6_current_address_postcode',
    'trust_party_6_current_address_street',
    'trust_party_6_current_address_town_city',
    'trust_party_6_date_of_birth',
    'trust_party_6_email_address',
    'trust_party_6_forenames',
    'trust_party_6_has_tax_residency_2',
    'trust_party_6_has_tax_residency_3',
    'trust_party_6_lived_at_current_address_3_years',
    'trust_party_6_national_client_identifier',
    'trust_party_6_previous_address_country',
    'trust_party_6_previous_address_county',
    'trust_party_6_previous_address_house_no_name',
    'trust_party_6_previous_address_postcode',
    'trust_party_6_previous_address_street',
    'trust_party_6_previous_address_town_city',
    'trust_party_6_second_country_of_nationality',
    'trust_party_6_surname',
    'trust_party_6_tax_residency_1_citizen',
    'trust_party_6_tax_residency_1_country',
    'trust_party_6_tax_residency_1_tax_resident',
    'trust_party_6_tax_residency_1_tin',
    'trust_party_6_tax_residency_2_citizen',
    'trust_party_6_tax_residency_2_country',
    'trust_party_6_tax_residency_2_tax_resident',
    'trust_party_6_tax_residency_2_tin',
    'trust_party_6_tax_residency_3_citizen',
    'trust_party_6_tax_residency_3_country',
    'trust_party_6_tax_residency_3_tax_resident',
    'trust_party_6_tax_residency_3_tin',
    'trust_party_6_telephone_number',
    'trust_party_6_title',
    'trust_party_6_title_other',
    'trust_party_6_town_of_birth',
    'trust_party_6_us_person',
    'trust_party_7_capacity',
    'trust_party_7_country_of_birth',
    'trust_party_7_country_of_nationality',
    'trust_party_7_current_address_country',
    'trust_party_7_current_address_county',
    'trust_party_7_current_address_house_no_name',
    'trust_party_7_current_address_postcode',
    'trust_party_7_current_address_street',
    'trust_party_7_current_address_town_city',
    'trust_party_7_date_of_birth',
    'trust_party_7_email_address',
    'trust_party_7_forenames',
    'trust_party_7_has_tax_residency_2',
    'trust_party_7_has_tax_residency_3',
    'trust_party_7_lived_at_current_address_3_years',
    'trust_party_7_national_client_identifier',
    'trust_party_7_previous_address_country',
    'trust_party_7_previous_address_county',
    'trust_party_7_previous_address_house_no_name',
    'trust_party_7_previous_address_postcode',
    'trust_party_7_previous_address_street',
    'trust_party_7_previous_address_town_city',
    'trust_party_7_second_country_of_nationality',
    'trust_party_7_surname',
    'trust_party_7_tax_residency_1_citizen',
    'trust_party_7_tax_residency_1_country',
    'trust_party_7_tax_residency_1_tax_resident',
    'trust_party_7_tax_residency_1_tin',
    'trust_party_7_tax_residency_2_citizen',
    'trust_party_7_tax_residency_2_country',
    'trust_party_7_tax_residency_2_tax_resident',
    'trust_party_7_tax_residency_2_tin',
    'trust_party_7_tax_residency_3_citizen',
    'trust_party_7_tax_residency_3_country',
    'trust_party_7_tax_residency_3_tax_resident',
    'trust_party_7_tax_residency_3_tin',
    'trust_party_7_telephone_number',
    'trust_party_7_title',
    'trust_party_7_title_other',
    'trust_party_7_town_of_birth',
    'trust_party_7_us_person',
    'trust_phone_number',
    'trust_purpose',
    'trust_registered_address_country',
    'trust_registered_address_county',
    'trust_registered_address_different',
    'trust_registered_address_house_no_name',
    'trust_registered_address_postcode',
    'trust_registered_address_street',
    'trust_registered_address_town_city',
    'trust_type',
    'trust_type_other',
    'upcoming_operation',
    'upcoming_operation_detail',
    'us_person',
    'worked_in_financial_industry',
];

/** Every statement id of the trust form 1.1 (SEP-293). */
export const FROZEN_TRUST_STATEMENT_IDS: readonly string[] = [
    'data_protection_note',
    'electronic_consent_1',
    'electronic_consent_2',
    'nominated_representative_declaration_1',
    'nominated_representative_declaration_2',
    'nominated_representative_declaration_3',
    'nominated_representative_declaration_4',
    'trust_client_classification_note_1',
    'trust_client_classification_note_2',
    'trust_client_classification_note_3',
    'trust_client_classification_note_4',
    'trust_declaration_1',
    'trust_declaration_2',
    'trust_declaration_3',
    'trust_declaration_4',
    'trust_declaration_5',
    'trust_declaration_6',
    'trust_declaration_6a',
    'trust_declaration_6b',
    'trust_declaration_6d',
    'trust_declaration_7',
    'trust_declaration_8',
    'trust_declaration_9',
    'trust_representatives_note_1',
    'trust_representatives_note_2',
    'trust_service_note_advisory',
    'trust_service_note_discretionary',
    'trust_service_note_execution_only',
];

/** Every question id of the SSAS form 1.0 (SEP-293). */
export const FROZEN_SSAS_QUESTION_IDS: readonly string[] = [
    'account_types',
    'additional_notes',
    'asset_business_interests',
    'asset_cash_savings_premium_bonds',
    'asset_main_property_value',
    'asset_other',
    'asset_other_detail',
    'asset_other_investments_value',
    'asset_other_property_value',
    'asset_pension_assets',
    'bank_account_currency',
    'bank_account_name',
    'bank_account_number',
    'bank_bic_aba',
    'bank_iban',
    'bank_name',
    'bank_sort_code',
    'capital_withdrawal_amount',
    'capital_withdrawal_expected',
    'capital_withdrawal_frequency',
    'capital_withdrawal_frequency_other',
    'commitment_discretionary_expenses',
    'commitment_essential_expenses',
    'commitment_mortgage_rent',
    'commitment_other_significant_expenses',
    'commitment_other_significant_expenses_detail',
    'commitment_school_fees',
    'country_of_birth',
    'country_of_nationality',
    'current_address_country',
    'current_address_county',
    'current_address_house_no_name',
    'current_address_moved_in_date',
    'current_address_postcode',
    'current_address_street',
    'current_address_town_city',
    'date_of_birth',
    'declarations_accepted',
    'electronic_communications_consent',
    'email_address',
    'forenames',
    'general_health',
    'general_health_detail',
    'has_financial_qualifications',
    'has_member_1',
    'has_member_2',
    'has_member_3',
    'has_member_4',
    'has_member_5',
    'has_member_6',
    'has_member_7',
    'has_tax_residency_2',
    'has_tax_residency_3',
    'income_all_other',
    'income_all_other_sources',
    'income_employment',
    'income_investment_outside_dowgate',
    'income_other_pension',
    'income_state_pension',
    'invested_in_aim',
    'investment_objective',
    'investment_restrictions',
    'knowledgeable_investor',
    'liability_anticipated_mortgage_payoff_date',
    'liability_credit_card_debt',
    'liability_mortgage_outstanding',
    'liability_other_debt',
    'liability_other_debt_detail',
    'marketing_company_news_and_events',
    'marketing_market_commentary_and_insights',
    'member_1_country_of_birth',
    'member_1_country_of_nationality',
    'member_1_current_address_country',
    'member_1_current_address_county',
    'member_1_current_address_house_no_name',
    'member_1_current_address_postcode',
    'member_1_current_address_street',
    'member_1_current_address_town_city',
    'member_1_date_of_birth',
    'member_1_email_address',
    'member_1_forenames',
    'member_1_has_tax_residency_2',
    'member_1_has_tax_residency_3',
    'member_1_lived_at_current_address_3_years',
    'member_1_national_client_identifier',
    'member_1_previous_address_country',
    'member_1_previous_address_county',
    'member_1_previous_address_house_no_name',
    'member_1_previous_address_postcode',
    'member_1_previous_address_street',
    'member_1_previous_address_town_city',
    'member_1_second_country_of_nationality',
    'member_1_surname',
    'member_1_tax_residency_1_citizen',
    'member_1_tax_residency_1_country',
    'member_1_tax_residency_1_tax_resident',
    'member_1_tax_residency_1_tin',
    'member_1_tax_residency_2_citizen',
    'member_1_tax_residency_2_country',
    'member_1_tax_residency_2_tax_resident',
    'member_1_tax_residency_2_tin',
    'member_1_tax_residency_3_citizen',
    'member_1_tax_residency_3_country',
    'member_1_tax_residency_3_tax_resident',
    'member_1_tax_residency_3_tin',
    'member_1_telephone_number',
    'member_1_title',
    'member_1_title_other',
    'member_1_town_of_birth',
    'member_1_us_person',
    'member_2_country_of_birth',
    'member_2_country_of_nationality',
    'member_2_current_address_country',
    'member_2_current_address_county',
    'member_2_current_address_house_no_name',
    'member_2_current_address_postcode',
    'member_2_current_address_street',
    'member_2_current_address_town_city',
    'member_2_date_of_birth',
    'member_2_email_address',
    'member_2_forenames',
    'member_2_has_tax_residency_2',
    'member_2_has_tax_residency_3',
    'member_2_lived_at_current_address_3_years',
    'member_2_national_client_identifier',
    'member_2_previous_address_country',
    'member_2_previous_address_county',
    'member_2_previous_address_house_no_name',
    'member_2_previous_address_postcode',
    'member_2_previous_address_street',
    'member_2_previous_address_town_city',
    'member_2_second_country_of_nationality',
    'member_2_surname',
    'member_2_tax_residency_1_citizen',
    'member_2_tax_residency_1_country',
    'member_2_tax_residency_1_tax_resident',
    'member_2_tax_residency_1_tin',
    'member_2_tax_residency_2_citizen',
    'member_2_tax_residency_2_country',
    'member_2_tax_residency_2_tax_resident',
    'member_2_tax_residency_2_tin',
    'member_2_tax_residency_3_citizen',
    'member_2_tax_residency_3_country',
    'member_2_tax_residency_3_tax_resident',
    'member_2_tax_residency_3_tin',
    'member_2_telephone_number',
    'member_2_title',
    'member_2_title_other',
    'member_2_town_of_birth',
    'member_2_us_person',
    'member_3_country_of_birth',
    'member_3_country_of_nationality',
    'member_3_current_address_country',
    'member_3_current_address_county',
    'member_3_current_address_house_no_name',
    'member_3_current_address_postcode',
    'member_3_current_address_street',
    'member_3_current_address_town_city',
    'member_3_date_of_birth',
    'member_3_email_address',
    'member_3_forenames',
    'member_3_has_tax_residency_2',
    'member_3_has_tax_residency_3',
    'member_3_lived_at_current_address_3_years',
    'member_3_national_client_identifier',
    'member_3_previous_address_country',
    'member_3_previous_address_county',
    'member_3_previous_address_house_no_name',
    'member_3_previous_address_postcode',
    'member_3_previous_address_street',
    'member_3_previous_address_town_city',
    'member_3_second_country_of_nationality',
    'member_3_surname',
    'member_3_tax_residency_1_citizen',
    'member_3_tax_residency_1_country',
    'member_3_tax_residency_1_tax_resident',
    'member_3_tax_residency_1_tin',
    'member_3_tax_residency_2_citizen',
    'member_3_tax_residency_2_country',
    'member_3_tax_residency_2_tax_resident',
    'member_3_tax_residency_2_tin',
    'member_3_tax_residency_3_citizen',
    'member_3_tax_residency_3_country',
    'member_3_tax_residency_3_tax_resident',
    'member_3_tax_residency_3_tin',
    'member_3_telephone_number',
    'member_3_title',
    'member_3_title_other',
    'member_3_town_of_birth',
    'member_3_us_person',
    'member_4_country_of_birth',
    'member_4_country_of_nationality',
    'member_4_current_address_country',
    'member_4_current_address_county',
    'member_4_current_address_house_no_name',
    'member_4_current_address_postcode',
    'member_4_current_address_street',
    'member_4_current_address_town_city',
    'member_4_date_of_birth',
    'member_4_email_address',
    'member_4_forenames',
    'member_4_has_tax_residency_2',
    'member_4_has_tax_residency_3',
    'member_4_lived_at_current_address_3_years',
    'member_4_national_client_identifier',
    'member_4_previous_address_country',
    'member_4_previous_address_county',
    'member_4_previous_address_house_no_name',
    'member_4_previous_address_postcode',
    'member_4_previous_address_street',
    'member_4_previous_address_town_city',
    'member_4_second_country_of_nationality',
    'member_4_surname',
    'member_4_tax_residency_1_citizen',
    'member_4_tax_residency_1_country',
    'member_4_tax_residency_1_tax_resident',
    'member_4_tax_residency_1_tin',
    'member_4_tax_residency_2_citizen',
    'member_4_tax_residency_2_country',
    'member_4_tax_residency_2_tax_resident',
    'member_4_tax_residency_2_tin',
    'member_4_tax_residency_3_citizen',
    'member_4_tax_residency_3_country',
    'member_4_tax_residency_3_tax_resident',
    'member_4_tax_residency_3_tin',
    'member_4_telephone_number',
    'member_4_title',
    'member_4_title_other',
    'member_4_town_of_birth',
    'member_4_us_person',
    'member_5_country_of_birth',
    'member_5_country_of_nationality',
    'member_5_current_address_country',
    'member_5_current_address_county',
    'member_5_current_address_house_no_name',
    'member_5_current_address_postcode',
    'member_5_current_address_street',
    'member_5_current_address_town_city',
    'member_5_date_of_birth',
    'member_5_email_address',
    'member_5_forenames',
    'member_5_has_tax_residency_2',
    'member_5_has_tax_residency_3',
    'member_5_lived_at_current_address_3_years',
    'member_5_national_client_identifier',
    'member_5_previous_address_country',
    'member_5_previous_address_county',
    'member_5_previous_address_house_no_name',
    'member_5_previous_address_postcode',
    'member_5_previous_address_street',
    'member_5_previous_address_town_city',
    'member_5_second_country_of_nationality',
    'member_5_surname',
    'member_5_tax_residency_1_citizen',
    'member_5_tax_residency_1_country',
    'member_5_tax_residency_1_tax_resident',
    'member_5_tax_residency_1_tin',
    'member_5_tax_residency_2_citizen',
    'member_5_tax_residency_2_country',
    'member_5_tax_residency_2_tax_resident',
    'member_5_tax_residency_2_tin',
    'member_5_tax_residency_3_citizen',
    'member_5_tax_residency_3_country',
    'member_5_tax_residency_3_tax_resident',
    'member_5_tax_residency_3_tin',
    'member_5_telephone_number',
    'member_5_title',
    'member_5_title_other',
    'member_5_town_of_birth',
    'member_5_us_person',
    'member_6_country_of_birth',
    'member_6_country_of_nationality',
    'member_6_current_address_country',
    'member_6_current_address_county',
    'member_6_current_address_house_no_name',
    'member_6_current_address_postcode',
    'member_6_current_address_street',
    'member_6_current_address_town_city',
    'member_6_date_of_birth',
    'member_6_email_address',
    'member_6_forenames',
    'member_6_has_tax_residency_2',
    'member_6_has_tax_residency_3',
    'member_6_lived_at_current_address_3_years',
    'member_6_national_client_identifier',
    'member_6_previous_address_country',
    'member_6_previous_address_county',
    'member_6_previous_address_house_no_name',
    'member_6_previous_address_postcode',
    'member_6_previous_address_street',
    'member_6_previous_address_town_city',
    'member_6_second_country_of_nationality',
    'member_6_surname',
    'member_6_tax_residency_1_citizen',
    'member_6_tax_residency_1_country',
    'member_6_tax_residency_1_tax_resident',
    'member_6_tax_residency_1_tin',
    'member_6_tax_residency_2_citizen',
    'member_6_tax_residency_2_country',
    'member_6_tax_residency_2_tax_resident',
    'member_6_tax_residency_2_tin',
    'member_6_tax_residency_3_citizen',
    'member_6_tax_residency_3_country',
    'member_6_tax_residency_3_tax_resident',
    'member_6_tax_residency_3_tin',
    'member_6_telephone_number',
    'member_6_title',
    'member_6_title_other',
    'member_6_town_of_birth',
    'member_6_us_person',
    'member_7_country_of_birth',
    'member_7_country_of_nationality',
    'member_7_current_address_country',
    'member_7_current_address_county',
    'member_7_current_address_house_no_name',
    'member_7_current_address_postcode',
    'member_7_current_address_street',
    'member_7_current_address_town_city',
    'member_7_date_of_birth',
    'member_7_email_address',
    'member_7_forenames',
    'member_7_has_tax_residency_2',
    'member_7_has_tax_residency_3',
    'member_7_lived_at_current_address_3_years',
    'member_7_national_client_identifier',
    'member_7_previous_address_country',
    'member_7_previous_address_county',
    'member_7_previous_address_house_no_name',
    'member_7_previous_address_postcode',
    'member_7_previous_address_street',
    'member_7_previous_address_town_city',
    'member_7_second_country_of_nationality',
    'member_7_surname',
    'member_7_tax_residency_1_citizen',
    'member_7_tax_residency_1_country',
    'member_7_tax_residency_1_tax_resident',
    'member_7_tax_residency_1_tin',
    'member_7_tax_residency_2_citizen',
    'member_7_tax_residency_2_country',
    'member_7_tax_residency_2_tax_resident',
    'member_7_tax_residency_2_tin',
    'member_7_tax_residency_3_citizen',
    'member_7_tax_residency_3_country',
    'member_7_tax_residency_3_tax_resident',
    'member_7_tax_residency_3_tin',
    'member_7_telephone_number',
    'member_7_title',
    'member_7_title_other',
    'member_7_town_of_birth',
    'member_7_us_person',
    'mobile_number',
    'national_client_identifier',
    'ongoing_account_funding_amount',
    'ongoing_account_funding_expected',
    'ongoing_account_funding_frequency',
    'ongoing_account_funding_frequency_other',
    'other_health_conditions',
    'other_health_conditions_detail',
    'other_product_experience',
    'portal_security_answer_childhood_road',
    'portal_security_answer_fathers_first_name',
    'portal_security_answer_favourite_colour',
    'portal_security_answer_first_car',
    'portal_security_answer_first_pet',
    'portal_security_answer_holiday_destination',
    'portal_security_answer_mothers_maiden_name',
    'portfolio_duration',
    'preferred_investment_manager',
    'previous_address_country',
    'previous_address_county',
    'previous_address_house_no_name',
    'previous_address_moved_in_date',
    'previous_address_postcode',
    'previous_address_street',
    'previous_address_town_city',
    'product_experience_alternatives',
    'product_experience_cash_deposits',
    'product_experience_derivatives',
    'product_experience_equities',
    'product_experience_fixed_income',
    'product_experience_funds',
    'regular_withdrawals_amount',
    'regular_withdrawals_frequency',
    'regular_withdrawals_frequency_other',
    'regular_withdrawals_needed',
    'risk_capacity_for_loss',
    'risk_comfortable_with_smaller_companies',
    'risk_income_change_over_ten_years',
    'risk_income_security',
    'risk_investment_horizon',
    'risk_liabilities_change_over_ten_years',
    'risk_liquid_funds_months_of_expenses',
    'risk_loss_that_prompts_move_to_cash',
    'risk_prefer_fewer_ups_and_downs',
    'risk_protection_over_returns',
    'risk_response_to_quarter_fall',
    'risk_understands_smaller_company_risks',
    'scheme_administrator_contact_email',
    'scheme_administrator_contact_forenames',
    'scheme_administrator_contact_phone',
    'scheme_administrator_contact_surname',
    'scheme_administrator_correspondence_address_country',
    'scheme_administrator_correspondence_address_county',
    'scheme_administrator_correspondence_address_different',
    'scheme_administrator_correspondence_address_house_no_name',
    'scheme_administrator_correspondence_address_postcode',
    'scheme_administrator_correspondence_address_street',
    'scheme_administrator_correspondence_address_town_city',
    'scheme_administrator_country_of_incorporation',
    'scheme_administrator_date_of_incorporation',
    'scheme_administrator_lei',
    'scheme_administrator_name',
    'scheme_administrator_registered_address_country',
    'scheme_administrator_registered_address_county',
    'scheme_administrator_registered_address_house_no_name',
    'scheme_administrator_registered_address_postcode',
    'scheme_administrator_registered_address_street',
    'scheme_administrator_registered_address_town_city',
    'scheme_administrator_registration_number',
    'scheme_administrator_status',
    'scheme_hmrc_pension_number',
    'scheme_name',
    'second_country_of_nationality',
    'service_type',
    'source_of_funds_business_detail',
    'source_of_funds_employment_detail',
    'source_of_funds_gift_detail',
    'source_of_funds_inheritance_detail',
    'source_of_funds_other_detail',
    'source_of_funds_property_detail',
    'source_of_funds_savings_investments_detail',
    'source_of_wealth_business_detail',
    'source_of_wealth_employment_detail',
    'source_of_wealth_gift_detail',
    'source_of_wealth_inheritance_detail',
    'source_of_wealth_other_detail',
    'source_of_wealth_property_detail',
    'source_of_wealth_savings_investments_detail',
    'source_types',
    'surname',
    'taken_part_in_placing_or_new_issue',
    'tax_residency_1_citizen',
    'tax_residency_1_country',
    'tax_residency_1_tax_resident',
    'tax_residency_1_tin',
    'tax_residency_2_citizen',
    'tax_residency_2_country',
    'tax_residency_2_tax_resident',
    'tax_residency_2_tin',
    'tax_residency_3_citizen',
    'tax_residency_3_country',
    'tax_residency_3_tax_resident',
    'tax_residency_3_tin',
    'third_address_country',
    'third_address_county',
    'third_address_house_no_name',
    'third_address_moved_in_date',
    'third_address_postcode',
    'third_address_street',
    'third_address_town_city',
    'third_party_address',
    'third_party_company_name',
    'third_party_correspondence',
    'third_party_correspondence_other',
    'third_party_email',
    'third_party_forenames',
    'third_party_postcode',
    'third_party_role',
    'third_party_role_other',
    'third_party_surname',
    'third_party_telephone',
    'third_party_title',
    'third_party_title_other',
    'title',
    'title_other',
    'town_of_birth',
    'upcoming_operation',
    'upcoming_operation_detail',
    'us_person',
    'worked_in_financial_industry',
];

/** Every statement id of the SSAS form 1.0 (SEP-293). */
export const FROZEN_SSAS_STATEMENT_IDS: readonly string[] = [
    'data_protection_note',
    'electronic_consent_1',
    'electronic_consent_2',
    'ssas_client_classification_note_1',
    'ssas_client_classification_note_2',
    'ssas_client_classification_note_3',
    'ssas_client_classification_note_4',
    'ssas_declaration_1',
    'ssas_declaration_2',
    'ssas_declaration_3',
    'ssas_declaration_4',
    'ssas_declaration_5',
    'ssas_declaration_6',
    'ssas_declaration_6a',
    'ssas_declaration_6b',
    'ssas_declaration_6d',
    'ssas_declaration_7',
    'ssas_declaration_8',
    'ssas_declaration_9',
    'ssas_service_note_advisory',
    'ssas_service_note_discretionary',
    'ssas_service_note_execution_only',
];

/** Every question id of the SIPP form 1.0 (SEP-293). */
export const FROZEN_SIPP_QUESTION_IDS: readonly string[] = [
    'account_types',
    'additional_notes',
    'asset_business_interests',
    'asset_cash_savings_premium_bonds',
    'asset_main_property_value',
    'asset_other',
    'asset_other_detail',
    'asset_other_investments_value',
    'asset_other_property_value',
    'asset_pension_assets',
    'bank_account_currency',
    'bank_account_name',
    'bank_account_number',
    'bank_bic_aba',
    'bank_iban',
    'bank_name',
    'bank_sort_code',
    'business_number',
    'capital_withdrawal_amount',
    'capital_withdrawal_expected',
    'capital_withdrawal_frequency',
    'capital_withdrawal_frequency_other',
    'commitment_discretionary_expenses',
    'commitment_essential_expenses',
    'commitment_mortgage_rent',
    'commitment_other_significant_expenses',
    'commitment_other_significant_expenses_detail',
    'commitment_school_fees',
    'country_of_birth',
    'country_of_nationality',
    'current_address_country',
    'current_address_county',
    'current_address_house_no_name',
    'current_address_moved_in_date',
    'current_address_postcode',
    'current_address_street',
    'current_address_town_city',
    'date_of_birth',
    'declarations_accepted',
    'dependent_1_date_of_birth',
    'dependent_1_name',
    'dependent_1_nature',
    'dependent_1_relationship',
    'dependent_2_date_of_birth',
    'dependent_2_name',
    'dependent_2_nature',
    'dependent_2_relationship',
    'dependent_3_date_of_birth',
    'dependent_3_name',
    'dependent_3_nature',
    'dependent_3_relationship',
    'dependent_4_date_of_birth',
    'dependent_4_name',
    'dependent_4_nature',
    'dependent_4_relationship',
    'electronic_communications_consent',
    'email_address',
    'employer_name',
    'employment_status',
    'employment_status_other',
    'forenames',
    'general_health',
    'general_health_detail',
    'has_financial_dependents',
    'has_financial_qualifications',
    'has_tax_residency_2',
    'has_tax_residency_3',
    'home_number',
    'income_all_other',
    'income_all_other_sources',
    'income_employment',
    'income_investment_outside_dowgate',
    'income_other_pension',
    'income_state_pension',
    'invested_in_aim',
    'investment_objective',
    'investment_restrictions',
    'job_title',
    'knowledgeable_investor',
    'liability_anticipated_mortgage_payoff_date',
    'liability_credit_card_debt',
    'liability_mortgage_outstanding',
    'liability_other_debt',
    'liability_other_debt_detail',
    'marital_status',
    'marketing_company_news_and_events',
    'marketing_market_commentary_and_insights',
    'mobile_number',
    'national_client_identifier',
    'ongoing_account_funding_amount',
    'ongoing_account_funding_expected',
    'ongoing_account_funding_frequency',
    'ongoing_account_funding_frequency_other',
    'other_health_conditions',
    'other_health_conditions_detail',
    'other_product_experience',
    'portal_security_answer_childhood_road',
    'portal_security_answer_fathers_first_name',
    'portal_security_answer_favourite_colour',
    'portal_security_answer_first_car',
    'portal_security_answer_first_pet',
    'portal_security_answer_holiday_destination',
    'portal_security_answer_mothers_maiden_name',
    'portfolio_duration',
    'preferred_investment_manager',
    'previous_address_country',
    'previous_address_county',
    'previous_address_house_no_name',
    'previous_address_moved_in_date',
    'previous_address_postcode',
    'previous_address_street',
    'previous_address_town_city',
    'product_experience_alternatives',
    'product_experience_cash_deposits',
    'product_experience_derivatives',
    'product_experience_equities',
    'product_experience_fixed_income',
    'product_experience_funds',
    'regular_withdrawals_amount',
    'regular_withdrawals_frequency',
    'regular_withdrawals_frequency_other',
    'regular_withdrawals_needed',
    'risk_capacity_for_loss',
    'risk_comfortable_with_smaller_companies',
    'risk_income_change_over_ten_years',
    'risk_income_security',
    'risk_investment_horizon',
    'risk_liabilities_change_over_ten_years',
    'risk_liquid_funds_months_of_expenses',
    'risk_loss_that_prompts_move_to_cash',
    'risk_prefer_fewer_ups_and_downs',
    'risk_protection_over_returns',
    'risk_response_to_quarter_fall',
    'risk_understands_smaller_company_risks',
    'second_country_of_nationality',
    'service_type',
    'sipp_account_number',
    'sipp_provider_contact_email',
    'sipp_provider_contact_forenames',
    'sipp_provider_contact_phone',
    'sipp_provider_contact_surname',
    'sipp_provider_correspondence_address_country',
    'sipp_provider_correspondence_address_county',
    'sipp_provider_correspondence_address_different',
    'sipp_provider_correspondence_address_house_no_name',
    'sipp_provider_correspondence_address_postcode',
    'sipp_provider_correspondence_address_street',
    'sipp_provider_correspondence_address_town_city',
    'sipp_provider_country_of_incorporation',
    'sipp_provider_date_of_incorporation',
    'sipp_provider_lei',
    'sipp_provider_name',
    'sipp_provider_registered_address_country',
    'sipp_provider_registered_address_county',
    'sipp_provider_registered_address_house_no_name',
    'sipp_provider_registered_address_postcode',
    'sipp_provider_registered_address_street',
    'sipp_provider_registered_address_town_city',
    'sipp_provider_registration_number',
    'sipp_provider_status',
    'source_of_funds_business_detail',
    'source_of_funds_employment_detail',
    'source_of_funds_gift_detail',
    'source_of_funds_inheritance_detail',
    'source_of_funds_other_detail',
    'source_of_funds_property_detail',
    'source_of_funds_savings_investments_detail',
    'source_of_wealth_business_detail',
    'source_of_wealth_employment_detail',
    'source_of_wealth_gift_detail',
    'source_of_wealth_inheritance_detail',
    'source_of_wealth_other_detail',
    'source_of_wealth_property_detail',
    'source_of_wealth_savings_investments_detail',
    'source_types',
    'surname',
    'taken_part_in_placing_or_new_issue',
    'tax_residency_1_citizen',
    'tax_residency_1_country',
    'tax_residency_1_tax_resident',
    'tax_residency_1_tin',
    'tax_residency_2_citizen',
    'tax_residency_2_country',
    'tax_residency_2_tax_resident',
    'tax_residency_2_tin',
    'tax_residency_3_citizen',
    'tax_residency_3_country',
    'tax_residency_3_tax_resident',
    'tax_residency_3_tin',
    'third_address_country',
    'third_address_county',
    'third_address_house_no_name',
    'third_address_moved_in_date',
    'third_address_postcode',
    'third_address_street',
    'third_address_town_city',
    'third_party_address',
    'third_party_company_name',
    'third_party_correspondence',
    'third_party_correspondence_other',
    'third_party_email',
    'third_party_forenames',
    'third_party_postcode',
    'third_party_role',
    'third_party_role_other',
    'third_party_surname',
    'third_party_telephone',
    'third_party_title',
    'third_party_title_other',
    'title',
    'title_other',
    'town_of_birth',
    'upcoming_operation',
    'upcoming_operation_detail',
    'us_person',
    'worked_in_financial_industry',
];

/** Every statement id of the SIPP form 1.0 (SEP-293). */
export const FROZEN_SIPP_STATEMENT_IDS: readonly string[] = [
    'data_protection_note',
    'electronic_consent_1',
    'electronic_consent_2',
    'sipp_client_classification_note_1',
    'sipp_client_classification_note_2',
    'sipp_client_classification_note_3',
    'sipp_client_classification_note_4',
    'sipp_declaration_1',
    'sipp_declaration_2',
    'sipp_declaration_3',
    'sipp_declaration_4',
    'sipp_declaration_5',
    'sipp_declaration_6',
    'sipp_declaration_6a',
    'sipp_declaration_6b',
    'sipp_declaration_6d',
    'sipp_declaration_7',
    'sipp_declaration_8',
    'sipp_declaration_9',
    'sipp_service_note_advisory',
    'sipp_service_note_discretionary',
    'sipp_service_note_execution_only',
];

/**
 * The frozen ids of each form (SEP-293). Each form has its own lists, because
 * each form has its own bank. The individual form holds the two lists above.
 * A later slice adds the lists of each form that it adds.
 */
/** The frozen lists of each form. */
type FrozenIdsByForm = Readonly<
    Record<
        | 'individual'
        | 'company_discretionary_advisory'
        | 'company_execution_only'
        | 'trust'
        | 'ssas'
        | 'sipp',
        { readonly questions: readonly string[]; readonly statements: readonly string[] }
    >
>;

// The form ids are snake_case values, so a list of pairs holds them and not
// the keys of an object literal (the snake-case guard of PRD #278).
export const FROZEN_CLIENT_APPLICATION_IDS_BY_FORM: FrozenIdsByForm = Object.fromEntries([
    [
        'individual',
        {
            questions: FROZEN_CLIENT_APPLICATION_QUESTION_IDS,
            statements: FROZEN_CLIENT_APPLICATION_STATEMENT_IDS,
        },
    ],
    [
        'company_discretionary_advisory',
        {
            questions: FROZEN_COMPANY_DISCRETIONARY_ADVISORY_QUESTION_IDS,
            statements: FROZEN_COMPANY_DISCRETIONARY_ADVISORY_STATEMENT_IDS,
        },
    ],
    [
        'company_execution_only',
        {
            questions: FROZEN_COMPANY_EXECUTION_ONLY_QUESTION_IDS,
            statements: FROZEN_COMPANY_EXECUTION_ONLY_STATEMENT_IDS,
        },
    ],
    ['trust', { questions: FROZEN_TRUST_QUESTION_IDS, statements: FROZEN_TRUST_STATEMENT_IDS }],
    ['ssas', { questions: FROZEN_SSAS_QUESTION_IDS, statements: FROZEN_SSAS_STATEMENT_IDS }],
    ['sipp', { questions: FROZEN_SIPP_QUESTION_IDS, statements: FROZEN_SIPP_STATEMENT_IDS }],
]) as FrozenIdsByForm;
